/**
 * The desktop QR card's rules — WEB ONLY, DESKTOP ONLY.
 *
 * The free web build already points at the stores twice: the badge row beside the menu's Play
 * button, and the buy card after every game once four stars are earned (src/game/promo.ts). Both
 * are links, and a link is the right shape of ask on a phone — the device reading it is the
 * device that would install the app.
 *
 * On a desktop it is the wrong shape. A parent playing this in a browser on a laptop cannot
 * install a phone app from that machine; clicking a badge there opens a store page on a device
 * that cannot act on it, and the purchase has to survive being remembered until they next pick up
 * their phone. A QR code closes exactly that gap, and only that one: it moves the offer onto the
 * device that can take it.
 *
 * Which is why this is desktop-only and not a third ask everywhere. On a phone a QR code is
 * useless — nobody scans their own screen — and phones are precisely where `storeTarget()` already
 * returns an answer and the existing badges already work. The two are complements, not rivals:
 * this module says yes almost exactly where `detectStoreTarget()` says "I don't know".
 *
 * Nothing here ever renders inside the paid apps; `isWeb()` gates the entry point, exactly as the
 * badges and the buy card do.
 */
import { devFlag } from '@/game/dev';
import { isWeb } from '@/game/promo';
import type { DeviceSignals } from '@/game/storeTarget';

/**
 * Where every desktop QR points, and the reason there is only one code (public/get.html).
 *
 * **The scanning device decides the store, and nothing else does.** That is the whole design. A
 * desktop cannot know which store its household buys from — and the two guesses available are both
 * wrong often enough to matter: two codes side by side ask the visitor a question at the moment we
 * want an answer, and "a Mac means an iPhone" is a guess that sends every Mac-with-an-Android
 * family to a store they cannot buy from. The phone holding the camera has the answer already, so
 * the link defers to it: one code, resolved one device later, by the device that knows.
 *
 * Absolute and hardcoded to production on purpose. A QR is read by a different device on a
 * different network, so a relative URL is meaningless and a staging origin would be unreachable —
 * a code scanned from a staging build should still lead to the real store.
 */
export const GET_URL = 'https://abcsmash.rcms.ch/get';

/**
 * The one committed code, rendered by `npm run qr` (scripts/render-qr.mjs).
 *
 * Shipped as a file rather than generated in the browser: it encodes a single URL that changes
 * about once a year, so bundling a QR library for every player to re-derive the same square on
 * every load would be a poor trade. `qr-assets.test.ts` fails if it stops matching GET_URL.
 */
export const QR_IMAGE = '/images/qr/get.svg';

/**
 * A machine with no store of its own — the visitor this card is for.
 *
 * Deliberately its own rules rather than `detectStoreTarget(s) === null`: that function's null also
 * covers "an Android phone we somehow failed to recognise", and drawing a QR code on a phone is
 * worse than drawing nothing. So this asks the positive question — no touch, no mobile UA — and
 * answers false whenever it is unsure.
 *
 * The `Macintosh` + `maxTouchPoints` pair is the same iPadOS 13+ trap storeTarget.ts documents: an
 * iPad calls itself a Mac, and touch points are the only tell. Here it matters twice over, because
 * the iPad is both a device that must not get a QR code and one that already has a good badge.
 */
export function isDesktop(s: DeviceSignals): boolean {
  if (/Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle/i.test(s.ua)) return false;
  if (s.uaDataPlatform === 'Android') return false;
  if (/Macintosh/i.test(s.ua) && s.maxTouchPoints > 1) return false;
  // A desktop browser reports 0; a touchscreen laptop reports 10 and is still a desktop, so this
  // is not a touch test. It is the iPad test above, and nothing else.
  return true;
}

interface UaData { platform?: string }

/** The live answer for this browser. False in a non-browser environment, like every node test. */
export function isDesktopBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const uaData = (navigator as Navigator & { userAgentData?: UaData }).userAgentData;
  return isDesktop({
    ua: navigator.userAgent ?? '',
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    uaDataPlatform: uaData?.platform,
  });
}

/**
 * Whether a round has ever finished in this browser.
 *
 * The card waits for it because an ad for the app is worth nothing to someone who has not yet seen
 * the game — the first screen should be the game, not a pitch. One round is the smallest amount of
 * "I have actually played this" there is, which is the right bar for something this small and this
 * far out of the way; the buy card's four stars is the bar for the full-size ask.
 *
 * localStorage, not sessionStorage: a visitor who played yesterday has already cleared it, and
 * making them replay a round to see the offer again would be theatre.
 */
const ROUNDS_KEY = 'abcs.qr.rounds.v1';

export function hasPlayedRound(): boolean {
  try {
    return localStorage.getItem(ROUNDS_KEY) !== null;
  } catch {
    return false;   // storage blocked (private mode): the card simply stays off
  }
}

/** Called wherever a round ends. Idempotent, and never throws — this must not break a summary. */
export function recordRoundPlayed(): void {
  try {
    localStorage.setItem(ROUNDS_KEY, '1');
  } catch { /* storage blocked — the card stays off, which is the safe direction */ }
}

/**
 * Dismissal, deliberately in MEMORY and nowhere else — it dies with the page.
 *
 * The × means "not right now", not "never again", so a reload brings the card back. That is the
 * honest reading of a one-click dismissal on a free build whose job is to point at the paid one,
 * and it is also the one behaviour with no failure mode: nothing is written, so nothing can be
 * blocked in a private window, go stale, or outlive the reason it was set. sessionStorage looks
 * like the same thing and is not — it survives a reload, which is exactly the visit where the
 * card should be back.
 */
let dismissed = false;

export function isQrPromoDismissed(): boolean {
  return dismissed;
}

export function dismissQrPromo(): void {
  dismissed = true;
}

/**
 * Every gate in one place, like `shouldShowBuyPrompt`.
 *
 * `?qr` skips the played-a-round wait so the card can be reviewed without finishing a game — the
 * same dev affordance `?buy` is for the buy card, and inert on production hosts for the same reason
 * (devFlag, see src/game/dev.ts). It does NOT skip the web check or the desktop check: there is no
 * QR code to be had inside the paid apps, and none worth drawing on a phone.
 */
export function shouldShowQrPromo(): boolean {
  if (!isWeb() || !isDesktopBrowser()) return false;
  if (isQrPromoDismissed()) return false;
  return devFlag('qr') || hasPlayedRound();
}
